// Job-costing types — mirror the mongoose JobCostingEmbedController resources.
// Backend keys are snake_case; actions.ts maps them to the camelCase shapes below.

export type CostCategory =
  | 'labour'
  | 'materials'
  | 'subcontractor'
  | 'equipment'
  | 'permits'
  | 'other_direct';

// 'rocketplan_scope' is emitted by the MONGOOSE-FR-020 scope cost feed (per-company flag,
// default OFF). Inert until that ships — no live payload contains the value yet.
export type CostSource = 'qbo' | 'rocketplan_timecard' | 'rocketplan_equipment' | 'rocketplan_scope';

export type MatchConfidence = 'exact' | 'fuzzy' | 'unmatched';

export const COST_CATEGORIES: CostCategory[] = [
  'labour',
  'materials',
  'subcontractor',
  'equipment',
  'permits',
  'other_direct',
];

// Categories a QBO account/item may be mapped to. Mirrors the backend
// JobCost::QBO_MAPPABLE_CATEGORIES — excludes 'labour' because labour cost comes
// exclusively from RocketPlan timecards, so mapping a QBO account to labour would
// double-count wages (the backend 422-rejects 'labour' for mappings).
export const QBO_MAPPABLE_CATEGORIES: CostCategory[] = [
  'materials',
  'subcontractor',
  'equipment',
  'permits',
  'other_direct',
];

export const COST_CATEGORY_LABELS: Record<CostCategory, string> = {
  labour: 'Labour',
  materials: 'Materials',
  subcontractor: 'Subcontractor',
  equipment: 'Equipment',
  permits: 'Permits',
  other_direct: 'Other Direct',
};

export const COST_SOURCE_LABELS: Record<CostSource, string> = {
  qbo: 'QuickBooks',
  rocketplan_timecard: 'Timecard',
  rocketplan_equipment: 'Equipment',
  rocketplan_scope: 'Scope',
};

export interface JobProfitability {
  opportunityId: string;
  name: string | null;
  // RocketPlan project identity (from the linked Project via opportunity_id). Null when
  // no RP project is linked to the opportunity yet.
  rpJobNumber: string | null;
  address: string | null;
  pipelineId: string | null;
  pipelineStageId: string | null;
  status: string | null;
  revenue: number;
  totalCost: number;
  grossProfit: number;
  grossMarginPct: number | null;
  invoiced: number;
  paid: number;
  outstandingAr: number;
  revenueBasis: 'sold' | 'invoiced' | string;
  costsByCategory: Partial<Record<CostCategory, number>>;
  // Budgeting (MONGOOSE-FR-007). hasBudget=false → render budget/variance as "—".
  hasBudget: boolean;
  totalBudget: number;
  budgetVariance: number; // + = under budget, − = over budget
  budgetVariancePct: number | null;
  budgetsByCategory: Partial<Record<CostCategory, number>>;
  varianceByCategory: Partial<Record<CostCategory, number>>;
}

export type BudgetSource = 'manual' | 'scope' | 'qbo';

export interface JobBudget {
  id: string;
  opportunityId: string;
  projectId: string | null;
  costCategory: CostCategory;
  budgetedAmount: number;
  source: BudgetSource | string;
  notes: string | null;
}

/** Map of cost_category => amount. A null amount clears that category's budget. */
export type SaveJobBudgetsPayload = Partial<Record<CostCategory, number | null>>;

export interface CostLine {
  id: string;
  opportunityId: string;
  projectId: string | null;
  costCategory: CostCategory | string;
  source: CostSource | string;
  amount: number | null;
  txnDate: string | null;
  vendorName: string | null;
  description: string | null;
  quickbooksTxnType: string | null;
  matchConfidence: MatchConfidence | string | null;
  matchedBy: string | null;
}

export interface CostCategoryMapping {
  id: string;
  locationId: string;
  qboAccountId: string | null;
  qboAccountName: string | null;
  qboItemId: string | null;
  costCategory: CostCategory;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SaveCategoryMappingPayload {
  qboAccountId?: string;
  qboAccountName?: string;
  qboItemId?: string;
  costCategory: CostCategory;
}

/** Spatie paginator meta. */
export interface PaginationMeta {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface PipelineStage {
  id: string;
  name: string;
}

export interface Pipeline {
  id: string;
  name: string;
  stages: PipelineStage[];
}

export interface JobCostingProps {
  locationId: string;
  ssoToken: string;
}
